import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Layout, ResetConfirmationModal } from '../../../../components';
import AppContext from '../../../../contexts/AppContext';
import { handleApi, AudioSpinner, useToast } from '../../../../utils/utils';

const NewRole = () => {
  const navigate = useNavigate();
  const { userData } = useContext(AppContext);
  const { showToast, toastMessage } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  // useForm setup, defaulting to empty name and description
  const { register, handleSubmit, reset } = useForm({
    defaultValues: { name: '', description: '' },
  });

  const handleSave = async (data) => {
    // Validate that name and description are not empty
    if (!data.name || !data.description) {
      showToast('Name and description are required.', 'error');
      return;
    }

    setIsLoading(true);

    const apiEndpoint = 'roles';
    console.log('data sir ',data);

    try {
      const response = await handleApi(apiEndpoint, data, userData, 'POST');
      if (response && response.status === 200) {
        showToast('Role saved successfully!', 'success');
        reset(); // Reset the form after a successful save
      } else {
        showToast(response?.data?.message || 'Failed to save role.', 'error');
      }
    } catch (error) {
      showToast('An error occurred while saving the role.', 'error');
      console.error('Save error:', error);
    } finally {setIsLoading(false);}
  };

  const handleReset = () => setShowResetModal(true);

  const confirmReset = () => {
    reset(); // Reset form fields
    setShowResetModal(false); // Close the modal
    showToast('Role form has been reset!', 'info');
  };

  const cancelReset = () => setShowResetModal(false);

  return (
    <Layout>
      <div className="min-h-screen text-white p-2 sm:p-4">
        {toastMessage.show && (
          <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg shadow-md z-50
                        ${toastMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
            {toastMessage.message}
          </div>
        )}

        <div className="relative flex justify-between items-center mb-4">
          <h1 className="font-extrabold text-gray-800 tracking-wide flex-grow text-center">
            NEW ROLE
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="bg-gray-600 rounded-3xl shadow-lg p-2 sm:p-4 mx-auto max-w-md">
          {isLoading ? (
            <div className="flex flex-col items-center py-6">
              <AudioSpinner />
              <p className="mt-4 text-gray-400">Loading. Please wait...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(handleSave)}>
              <div className="space-y-6">
                <div className="bg-gray-500 px-4 py-4 rounded-xl text-cyan-200 w-full space-y-4">
                  <div className="flex flex-col items-center">
                    <label htmlFor="name" className="font-medium text-cyan-200 mb-1">Role Name</label>
                    <input
                      id="name"
                      type="text"
                      {...register('name')}
                      className="text-center w-full bg-gray-500 text-white border-b-2 focus:outline-none border-cyan-200 rounded-md p-1"
                      placeholder="Enter role name"
                    />
                  </div>
                  <div className="flex flex-col items-center">
                    <label htmlFor="description" className="font-medium text-cyan-200 mb-1">Role Description</label>
                    <textarea
                      id="description"
                      {...register('description')}
                      className="text-center w-full bg-gray-500 text-white border-b-2 focus:outline-none border-cyan-200 rounded-md p-1 min-h-[50px]"
                      placeholder="Enter role description"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between mt-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 rounded-xl font-semibold text-gray-800 bg-cyan-200 hover:bg-cyan-300 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl font-semibold text-gray-800 bg-cyan-200 hover:bg-cyan-300 transition-all duration-300"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <ResetConfirmationModal
        show={showResetModal}
        onConfirm={confirmReset}
        onCancel={cancelReset}
      />
    </Layout>
  );
};

export default NewRole;